/**
 * Custom hooks for Document Suggestions (Annotations/Polygons)
 * Handles CRUD operations for document annotations with real-time sync
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface PolygonCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
  normalized?: boolean;
}

export interface DocumentSuggestion {
  document_user_suggestion_id: string;
  document_id: string;
  company_id: string;
  created_by: string;
  // Content
  name: string;
  description: string | null;
  suggestion_type: string | null;
  // Location
  document_page: number;
  polygon_attributes: PolygonCoordinates | null;
  // Status
  status: 'pending' | 'in_review' | 'accepted' | 'rejected' | 'solved';
  priority: 'low' | 'medium' | 'high' | 'critical';
  // Resolution
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  // AI fields
  ai_suggested_value: string | null;
  ai_suggestion_confidence: number | null;
  ai_resolution_status: string | null;
  // Metadata
  attribute_metadata: Record<string, unknown> | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface CreateSuggestionRequest {
  name: string;
  description?: string;
  suggestion_type?: string;
  document_page?: number;
  polygon_coordinates?: PolygonCoordinates;
  priority?: string;
  attributes?: Record<string, unknown>;
}

export interface UpdateSuggestionRequest {
  name?: string;
  description?: string;
  suggestion_type?: string;
  document_page?: number;
  polygon_coordinates?: PolygonCoordinates;
  priority?: string;
  attributes?: Record<string, unknown>;
  status?: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const SUGGESTION_QUERY_KEYS = {
  all: ['document-suggestions'] as const,
  byDocument: (documentId: string) =>
    [...SUGGESTION_QUERY_KEYS.all, documentId] as const,
  single: (suggestionId: string) =>
    [...SUGGESTION_QUERY_KEYS.all, 'single', suggestionId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all suggestions for a document
 */
export function useDocumentSuggestions(documentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: SUGGESTION_QUERY_KEYS.byDocument(documentId || ''),
    queryFn: async (): Promise<DocumentSuggestion[]> => {
      return await apiGet<DocumentSuggestion[]>(
        `/documents/${documentId}/suggestions`
      );
    },
    enabled: enabled && !!documentId,
  });
}

/**
 * Fetch a single suggestion by ID
 */
export function useSuggestion(suggestionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: SUGGESTION_QUERY_KEYS.single(suggestionId || ''),
    queryFn: async (): Promise<DocumentSuggestion> => {
      return await apiGet<DocumentSuggestion>(
        `/suggestions/${suggestionId}`
      );
    },
    enabled: enabled && !!suggestionId,
  });
}

/**
 * Create a new suggestion (annotation/polygon)
 */
export function useCreateSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      ...data
    }: CreateSuggestionRequest & { documentId: string }): Promise<DocumentSuggestion> => {
      return await apiPost<DocumentSuggestion>(
        `/documents/${documentId}/suggestions`,
        data
      );
    },
    onSuccess: (result) => {
      // Invalidate document suggestions
      queryClient.invalidateQueries({
        queryKey: SUGGESTION_QUERY_KEYS.byDocument(result.document_id),
      });
    },
  });
}

/**
 * Update an existing suggestion
 */
export function useUpdateSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      ...data
    }: UpdateSuggestionRequest & { suggestionId: string }): Promise<DocumentSuggestion> => {
      return await apiPut<DocumentSuggestion>(
        `/suggestions/${suggestionId}`,
        data
      );
    },
    onSuccess: (result) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: SUGGESTION_QUERY_KEYS.byDocument(result.document_id),
      });
      queryClient.invalidateQueries({
        queryKey: SUGGESTION_QUERY_KEYS.single(result.document_user_suggestion_id),
      });
    },
  });
}

/**
 * Delete a suggestion (soft delete)
 */
export function useDeleteSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      documentId: _documentId,
    }: {
      suggestionId: string;
      documentId: string;
    }): Promise<void> => {
      await apiDelete(`/suggestions/${suggestionId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: SUGGESTION_QUERY_KEYS.byDocument(variables.documentId),
      });
    },
  });
}

/**
 * Update suggestion status only
 */
export function useUpdateSuggestionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      status,
      resolutionNotes,
    }: {
      suggestionId: string;
      status: string;
      resolutionNotes?: string;
    }): Promise<DocumentSuggestion> => {
      return await apiPut<DocumentSuggestion>(
        `/suggestions/${suggestionId}/status`,
        {
          status,
          resolution_notes: resolutionNotes,
        }
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: SUGGESTION_QUERY_KEYS.byDocument(result.document_id),
      });
    },
  });
}
